/**
 * deployGuard (28/09/2026) — mantém a aba aberta viva através de um deploy do front.
 *
 * O problema: o telão do NOC fica horas com o bundle antigo carregado. O build
 * troca os chunks (nome com hash) e a próxima tela lazy pede um arquivo que não
 * existe mais → import() falha → ErrorBoundary → telão parado até alguém dar F5.
 *
 * Três defesas:
 *  1. `vite:preloadError` e erro de chunk no ErrorBoundary → recarrega sozinho.
 *  2. Polling de /version.json (gerado no build): versão nova → recarrega quando
 *     a aba está ociosa ou em segundo plano; com operador ativo, só mostra aviso.
 *  3. (no servidor) scripts/build-atomic.sh mantém os assets dos builds recentes
 *     e troca o dist/ de uma vez, então a aba antiga nem chega a quebrar.
 *
 * Antes de recarregar confere se o servidor responde: recarregar com o nginx
 * fora troca a página do NMS pela página de erro do browser, e aí não volta mais.
 */

const CHECK_EVERY_MS = 60_000;
const IDLE_MS = 5 * 60_000;
const RETRY_MS = 15_000;
// Anti-loop: build quebrado (chunk faltando de verdade) não pode virar reload infinito.
const LOOP_WINDOW_MS = 2 * 60_000;
const LOOP_MAX = 3;
const LOOP_KEY = 'nms.deployGuard.reloads';

export function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as { message?: string } | null)?.message ?? err ?? '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|is not a valid JavaScript MIME type/i.test(msg);
}

async function fetchBuildId(): Promise<string | null> {
  try {
    const r = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!r.ok) return null;
    const j = (await r.json()) as { buildId?: string };
    return j.buildId ?? null;
  } catch {
    return null;
  }
}

function recentReloads(): number[] {
  try {
    const arr = JSON.parse(sessionStorage.getItem(LOOP_KEY) || '[]') as number[];
    return arr.filter((t) => Date.now() - t < LOOP_WINDOW_MS);
  } catch {
    return [];
  }
}

let pending = false;

/** Recarrega a página assim que o servidor responder. Devolve false se o anti-loop barrou. */
export function reloadWhenReady(reason: string): boolean {
  if (pending) return true;
  const recent = recentReloads();
  if (recent.length >= LOOP_MAX) {
    console.error(`[deployGuard] ${recent.length} reloads em ${LOOP_WINDOW_MS / 1000}s, parei (${reason})`);
    return false;
  }
  pending = true;
  const attempt = async () => {
    if ((await fetchBuildId()) === null) {
      setTimeout(attempt, RETRY_MS);
      return;
    }
    try { sessionStorage.setItem(LOOP_KEY, JSON.stringify([...recent, Date.now()])); } catch { /* ignore */ }
    console.warn(`[deployGuard] recarregando: ${reason}`);
    window.location.reload();
  };
  void attempt();
  return true;
}

function showBanner() {
  if (document.getElementById('nms-deploy-banner')) return;
  const el = document.createElement('div');
  el.id = 'nms-deploy-banner';
  el.style.cssText =
    'position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:9999;' +
    'background:#065f46;color:#ecfdf5;padding:8px 14px;border-radius:8px;font:14px system-ui,sans-serif;' +
    'box-shadow:0 4px 16px rgba(0,0,0,.35);display:flex;gap:12px;align-items:center';
  el.textContent = 'Nova versão do NMS publicada.';
  const btn = document.createElement('button');
  btn.textContent = '↻ Recarregar';
  btn.style.cssText = 'background:#10b981;color:#022c22;border:0;border-radius:6px;padding:4px 10px;cursor:pointer;font-weight:600';
  btn.onclick = () => reloadWhenReady('botão do aviso de versão');
  el.appendChild(btn);
  document.body.appendChild(el);
}

export function startDeployGuard() {
  // Chunk que falha no preload: o Vite relança o erro depois do evento, e o
  // ErrorBoundary também pega — o `pending` evita reload em dobro.
  window.addEventListener('vite:preloadError', () => { reloadWhenReady('vite:preloadError'); });

  if ((import.meta as any).env?.DEV) return;

  let lastActivity = Date.now();
  // Movimento de mouse não conta: o cursor parado em cima do telão não é uso.
  for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart']) {
    window.addEventListener(ev, () => { lastActivity = Date.now(); }, { passive: true, capture: true });
  }

  setInterval(async () => {
    const id = await fetchBuildId();
    if (!id || id === __BUILD_ID__) return;
    const idle = document.hidden || Date.now() - lastActivity > IDLE_MS;
    // Console SSH aberto: reload derrubaria a sessão do operador.
    const busy = !!document.querySelector('.xterm');
    if (idle && !busy) reloadWhenReady(`versão ${__BUILD_ID__} → ${id}`);
    else showBanner();
  }, CHECK_EVERY_MS);
}
